import type { PartnerGrade } from '~/api/model';
import type { CART_TYPE } from '~/hooks/useCartTypeParam';

export const ROOT_ROUTER = {
  root: '/app',
} as const;

export const ADDRESS_ROUTER = {
  root: '/addresses',
  new: (cartType?: CART_TYPE) =>
    `/addresses/new${cartType ? `?cartType=${cartType}` : ''}`,
  detail: (id: number) => `/addresses/${id}`,
} as const;

export const CLAIMS_ROUTER = {
  root: '/claims',
  detail: (id: number) => `/claims/${id}`,
} as const;

export const ORDERS_ROUTER = {
  root: '/orders',
  detail: (id: number) => `/orders/${id}`,
  /** 취소 요청 */
  cancel: (orderId: number, productOrderId: number) =>
    `/orders/${orderId}/cancel?productOrderId=${productOrderId}`,
  /** 취소 정보 */
  cancelInfo: (orderId: number, productOrderId: number) =>
    `/orders/${orderId}/cancel/${productOrderId}`,
  /** 교환/반품 요청 */
  refund: (vendorOrderId: number, productOrderId: number) =>
    `/orders/${vendorOrderId}/refund?productOrderId=${productOrderId}`,
} as const;

export const VENDORS_ROUTER = {
  vendorDetail: (id: number) => `/vendors/${id}`,
  productDetail: (vendorId: number, offerId: number) =>
    `/vendors/${vendorId}/products/${offerId}`,
  /** 상품 문의 작성 */
  productInquiry: (vendorId: number, offerId: number) =>
    `/vendors/${vendorId}/products/${offerId}?title=inquiry`,
} as const;

export const MYPAGE_ROUTER = {
  root: '/mypage',
  refundAccount: '/mypage/refund-account',
  inquiry: '/mypage/inquiries',
  coupons: '/mypage/coupons',
} as const;

export const OFFERS_ROUTER = {
  recentlyViewed: '/offers/recently-viewed',
  popularShowcase: '/offers/popular-showcase',
  frequentlyOrdered: '/offers/frequently-ordered',
  showcase: (code: string) => `/offers/showcase?code=${code}`,
  showcaseGroup: (code: string) => `/offers/showcase-group?code=${code}`,
  emptyShowcase: '/offers/showcase/empty',
} as const;

// 고정 URL인 이벤트 페이지만 등록
export const EVENT_ROUTER = {
  pmwk: '/event/plusweek',
  recommendDefault: '/event/recommend/default',
} as const;

export const SEARCH_ROUTER = {
  root: '/search',
  /**
   * @description openPageModal을 통해서 이동할 때만 사용
   * */
  searchText: (text: string) =>
    `/search?searchText=${encodeURIComponent(text)}`,
  productType: (productType: string) => `/search?productType=${productType}`,
} as const;

export const CASHNOTE_MEMBERSHIP_ROUTER = {
  membershipWeekEvent: '/cashnote-membership/promotion/weekly-event',
} as const;

export const CATEGORY_ROUTER = {
  root: '/category',
  categoryTwoDepth: (categoryId: number) => `/category/${categoryId}`,
  vendorCategory: (vendorId: string) => `/vendors/${vendorId}/category`,
  vendorCategoryTwoDepth: (vendorId: string, categoryId: number) =>
    `/vendors/${vendorId}/category/${categoryId}`,
} as const;

export const CAMPAIGN_ROUTER = {
  root: '/campaigns',
  campaignDetail: (campaginCode: string) => `/campaigns/${campaginCode}`,
} as const;

export const CHECKOUT_ROUTER = {
  root: '/checkout',
  quick: '/checkout?cartType=QUICK',
  newUserRecommend: (cartType: CART_TYPE) =>
    `/recommend/new?cartType=${cartType || ''}`,
  recommend: (cartType: CART_TYPE) => `/recommend?cartType=${cartType || ''}`,
  eCouponRoot: '/e-coupon/checkout?productType=E_COUPON',
  eCouponGift: (isGift?: boolean) =>
    `/e-coupon/checkout?productType=E_COUPON&${isGift ? 'gift=true' : ''}`,
} as const;

/**
 * @description 이동을 위한 브릿지 페이지 라우터
 * */
export const BRIDGE_ROUTER = {
  storeRecommend: '/bridge/store-recommend',
  weeklyEvent: '/bridge/weekly-event', // 플멤위크 이벤트 페이지
} as const;

export const EXTERNAL_ROUTER = {
  eCoupon: (encryptedOrderCode: string) =>
    `/external/e-coupon/${encryptedOrderCode}`,
  // FIXME: 경로를 각 상태에 따라서 분리 처리
  checkoutSuccessRedirect: `/public/app/kpn/modal-close`,
  checkoutFailRedirect: `/public/app/kpn/modal-close`,
  checkoutCloseRedirect: `/public/app/kpn/modal-close`,
  checkoutCancelRedirect: `/public/app/kpn/modal-close`,
} as const;

export const TAX_EASE_ROUTER = {
  root: '/tax-ease/home',
  accountants: {
    all: '/tax-ease/accountants/all',
    pro: '/tax-ease/accountants/pro',
    prime: '/tax-ease/accountants/prime',
    detail: (partnerId: number) => `/tax-ease/accountants/${partnerId}`,
  },
  form: {
    /** 일반 견적 신청 폼 */
    register: (
      partnerId?: number,
      quoteType: 'ONE_TO_ONE' | 'COMPARE' = 'COMPARE',
    ) =>
      `/tax-ease/form/register?${
        partnerId ? `targetPartnerIds=${partnerId}&` : ''
      }${quoteType ? `quoteType=${quoteType}` : ''}`,
    /** 멤버십 견적 신청 시작 페이지 - openModal로만 열어야함  */
    membership: (grade: PartnerGrade = 'PRIME') =>
      `/tax-ease/form/membership?grade=${grade}`,
    /** 멤버십 견적 신청 폼 */
    membershipRegister: (grade: PartnerGrade = 'PRIME') =>
      `/tax-ease/form/membership-register?grade=${grade}`,
    /** 견적 신청 완료 페이지 */
    complete: (
      quoteRequestId: number,
      type?: 'ONE_TO_ONE' | 'COMPARE' | PartnerGrade,
    ) =>
      `/tax-ease/form/complete/${quoteRequestId}${type ? `?type=${type}` : ''}`,
  },
  quotation: {
    /** 견적 상세 페이지 - openModal로 열어야 함. */
    detail: (quoteRequestId: number) => `/tax-ease/quotation/${quoteRequestId}`,
  },
} as const;

export const NOTIFICATION_ROUTER = {
  root: '/notification',
  /** 알림 - 상담 선택 */
  consultation: '/notification?tab=consultation',
  rootWithFilter: (filter: 'all' | 'product' | 'event' | 'estimate') =>
    `/notification?filter=${filter}`,
} as const;

/**
 * @description 이 페이지는 openPageModal을 통해서만 진입합니다.
 * @example openPageModal(CHATTING_ROUTER.detail(chatId, partnerId))
 */
export const CHATTING_ROUTER = {
  /** 채팅 상세 페이지 */
  detail: (chatId: string | number, partnerId: string | number) =>
    `/chatting/${chatId}/partner/${partnerId}`,
} as const;
